import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';

import ColGroup from 'components/ColGroup';

import styles from './DataTableOperation.module.scss';

const HEADS = [
  'Người thao tác',
  'Hạng mục',
  'Hành động',
  'Nội dung',
  'Thời gian thay đổi',
];

const COL_WIDTHS = [1.4, 1.6, 0.9, 4.2, 1.4];
const COL_WIDTH_SUM = COL_WIDTHS.reduce((sum, width) => sum + width, 0);
const BRACKET_PATTERN = /(\[[^\]]+\])/g;

const isBracketedText = (part) => part.startsWith('[') && part.endsWith(']');

const unwrapBracketText = (part) => part.slice(1, -1);

const getBracketClassName = (hasStatusChange, bracketIndex) => {
  if (!hasStatusChange) {
    return undefined;
  }

  if (bracketIndex === 2) {
    return styles.StatusFrom;
  }

  if (bracketIndex === 3) {
    return styles.StatusTo;
  }

  return undefined;
};

function OperationContent({ description }) {
  if (!description) {
    return <span>—</span>;
  }

  const parts = description.split(BRACKET_PATTERN).filter(Boolean);
  const hasStatusChange = description.includes('trạng thái');
  let offset = 0;
  let bracketIndex = 0;

  return (
    <span>
      {parts.map((part) => {
        const key = `${offset}-${part}`;
        offset += part.length;

        if (!isBracketedText(part)) {
          return <React.Fragment key={key}>{part}</React.Fragment>;
        }

        bracketIndex += 1;

        return (
          <strong
            key={key}
            className={getBracketClassName(hasStatusChange, bracketIndex)}
          >
            {unwrapBracketText(part)}
          </strong>
        );
      })}
    </span>
  );
}

OperationContent.propTypes = {
  description: PropTypes.string,
};

OperationContent.defaultProps = {
  description: '',
};

function DataTableOperation({ operations }) {
  return (
    <div
      className={styles.Component}
      style={{ '--operation-table-cols': COL_WIDTH_SUM }}
    >
      <Table responsive className="Table">
        <ColGroup
          list={COL_WIDTHS}
          containerWidth={COL_WIDTH_SUM}
          paddingX={0}
        />

        <thead className="Table__Header">
          <tr className="Table__Header__Row">
            {HEADS.map((head) => (
              <th key={head} className="Table__Header__Row__Content">
                {head}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="Table__Body">
          {operations.length > 0 ? (
            operations.map((operation, index) => (
              <tr
                className="Table__Body__Row"
                key={operation.id || `${operation.operatorName}-${operation.changedAt}-${index}`}
              >
                <td className="Table__Body__Row__Content">
                  {operation.operatorName || '—'}
                </td>
                <td className="Table__Body__Row__Content">
                  {operation.category || '—'}
                </td>
                <td className="Table__Body__Row__Content">
                  {operation.action || '—'}
                </td>
                <td className={`Table__Body__Row__Content ${styles.ContentCell}`}>
                  <OperationContent description={operation.description} />
                </td>
                <td className={`Table__Body__Row__Content ${styles.DateCell}`}>
                  {operation.changedAt || '—'}
                </td>
              </tr>
            ))
          ) : (
            <tr className="Table__Body__Row">
              <td
                colSpan={HEADS.length}
                className={`Table__Body__Row__Content text-center ${styles.EmptyCell}`}
              >
                <div className="NoData">Chưa có dữ liệu</div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

DataTableOperation.propTypes = {
  operations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      operatorName: PropTypes.string,
      category: PropTypes.string,
      action: PropTypes.string,
      description: PropTypes.string,
      changedAt: PropTypes.string,
    }),
  ),
};

DataTableOperation.defaultProps = {
  operations: [],
};

export default memo(DataTableOperation);
